import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import {
    ICoverageHistoryAggregationsFlags,
    ICoverageHistoryAggregationsMedicaidActivePlanType,
    ICoverageHistoryAggregationsMedicaidPlanType,
    ICoverageHistoryAggregationsStatusCodes,
    ICoverageHistoryListSort,
} from "@common-shared/types/internalApis/coverageHistory.types"
import { IOneVerifyEhrWriteStatusUpdatedSocketsPayload } from "@common-shared/types/object.types"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types"
import {
    ICoverageHistoryAggregationsPlanYearResets,
    IOneVerifyCoverageCheckHistory,
    IOneVerifyPolicyComment,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { PoliciesListHelper } from "../../helpers/policiesList.helper"
import { UtilHelper } from "../../helpers/util.helper"
import type { ICoverageHistoryListResult } from "../../types/data.types"
import type { IPoliciesState } from "../../types/state.types"
import {
    policiesGetAgenticList,
    policiesGetAssignedTasksList,
    policiesGetHistoryList,
    policiesGetMedicaidList,
    policiesGetMedicaidReportsAggregationsActivePlanType,
    policiesGetMedicaidReportsAggregationsPlanType,
    policiesGetOverridesList,
    policiesGetPlanYearResetsAggregations,
    policiesGetPlanYearResetsList,
    policiesGetReportsAggregationsFlags,
    policiesGetReportsAggregationsStatusCodes,
    policiesGetReportsList,
} from "../thunks/policies.thunks"
import { taskAssignmentsGetList } from "../thunks/taskAssignments.thunks"

const initialState: IPoliciesState = {
    // GENERAL
    visualizationsCollapseTrigger: null,
    policiesHeaderDragDropDisabled: false,
    policiesGettingFullListProgress: 0,
    // SINGLE POLICY
    selectedItem: null,
    selectedItemComments: [],
    selectedItemHistory: [],
    selectedItemHistoryPagination: UtilHelper.getPagination(),
    // SEARCH / HISTORY
    historyItems: [],
    historyFilters: PoliciesListHelper.getDefaultFiltersConfiguration(
        PoliciesListType.HISTORY_SEARCH,
    ),
    historySort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.HISTORY_SEARCH,
    ),
    historyPagination: UtilHelper.getPagination(),
    historyIsError: false,
    // REPORTS
    reportsItems: [],
    reportsPagination: UtilHelper.getPagination(),
    reportsFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.REPORTS,
    ),
    reportsSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.REPORTS,
    ),
    reportsIsError: false,
    reportsAggregationsStatusCodes: null,
    reportsAggregationsFlags: null,
    // OVERRIDES
    overridesItems: [],
    overridesPagination: UtilHelper.getPagination(),
    overridesFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.OVERRIDES,
    ),
    overridesSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.OVERRIDES,
    ),
    overridesIsError: false,
    // PLAN YEAR RESETS
    planYearResetsItems: [],
    planYearResetsPagination: UtilHelper.getPagination(),
    planYearResetsFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.PLAN_YEAR_RESETS,
    ),
    planYearResetsSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.PLAN_YEAR_RESETS,
    ),
    planYearResetsIsError: false,
    planYearResetsAggregations: null,
    // MEDICAID
    medicaidItems: [],
    medicaidPagination: UtilHelper.getPagination(),
    medicaidFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.MEDICAID,
    ),
    medicaidSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.MEDICAID,
    ),
    medicaidIsError: false,
    medicaidAggregationsPlanType: null,
    medicaidAggregationsActivePlanType: null,
    // AGENTIC
    agenticItems: [],
    agenticPagination: UtilHelper.getPagination(),
    agenticFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.AGENTIC,
    ),
    agenticSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.AGENTIC,
    ),
    agenticIsError: false,
    // TASKS assignments
    taskAssignmentsItems: [],
    taskAssignmentsPagination: UtilHelper.getPagination(),
    taskAssignmentsFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.TASK_ASSIGNMENTS,
    ),
    taskAssignmentsSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.TASK_ASSIGNMENTS,
    ),
    taskAssignmentsIsError: false,
    // assigned TASKS
    assignedTasksItems: [],
    assignedTasksPagination: UtilHelper.getPagination(),
    assignedTasksFilters: PoliciesListHelper.getSavedFiltersConfiguration(
        PoliciesListType.ASSIGNED_TASKS,
    ),
    assignedTasksSort: PoliciesListHelper.getSavedSortConfiguration(
        PoliciesListType.ASSIGNED_TASKS,
    ),
    assignedTasksIsError: false,
}

const policiesSlice = createSlice({
    name: "policies",
    initialState,
    reducers: {
        policiesReset: () => initialState,

        policiesSetVisualizationsCollapseTrigger: (
            state,
            action: PayloadAction<number | null | undefined>,
        ) => {
            state.visualizationsCollapseTrigger =
                typeof action.payload === "undefined"
                    ? new Date().getTime()
                    : action.payload
        },

        policiesSetHeaderDragDropDisabled: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.policiesHeaderDragDropDisabled = action.payload
        },

        policiesSetGettingFullListProgress: (
            state,
            action: PayloadAction<number>,
        ) => {
            state.policiesGettingFullListProgress = action.payload
        },

        policiesIncreaseGetFullListProgress: state => {
            // Don't auto increase if new value > 99
            const newValue = state.policiesGettingFullListProgress + 1

            if (newValue < 100) {
                state.policiesGettingFullListProgress = newValue
            }
        },

        // SINGLE POLICY

        policiesResetSelectedItem: state => {
            state.selectedItem = null
            state.selectedItemComments = []
            state.selectedItemHistory = []
            state.selectedItemHistoryPagination = UtilHelper.getPagination()
        },

        policiesSetSelectedItem: (
            state,
            action: PayloadAction<IOneVerifyCoverageCheckHistory | null>,
        ) => {
            state.selectedItem = action.payload
        },

        policiesAddSelectedItemHistory: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            // Deduplication is needed because technically new checks can be made for a policy while the user is viewing it,
            // in which case the pagination start value will be behind, leading to duplicates
            state.selectedItemHistory = [
                ...state.selectedItemHistory,
                ...action.payload.items.filter(
                    newItem =>
                        !state.selectedItemHistory.some(
                            existingItem =>
                                existingItem.coverageResult.resultId ===
                                newItem.coverageResult.resultId,
                        ),
                ),
            ]

            state.selectedItemHistoryPagination = action.payload.newPagination
        },

        /**
         * Update a single item in the selected item history list
         * Note: We could alter this to accept an array of items if we needed to in the future
         */
        policiesUpdateSelectedItemHistory: (
            state,
            action: PayloadAction<IOneVerifyCoverageCheckHistory>,
        ) => {
            state.selectedItemHistory = state.selectedItemHistory.map(item =>
                item.coverageResult.resultId ===
                action.payload.coverageResult.resultId
                    ? action.payload
                    : item,
            )
        },

        policiesSetSelectedItemComments: (
            state,
            action: PayloadAction<IOneVerifyPolicyComment[]>,
        ) => {
            state.selectedItemComments = action.payload
        },

        policiesAddedSelectedItemComments: (
            state,
            action: PayloadAction<IOneVerifyPolicyComment>,
        ) => {
            const { replyToComment } = action.payload

            const isExistingComment = (
                list: IOneVerifyPolicyComment[],
                newComment: IOneVerifyPolicyComment,
            ): boolean => {
                return !!list.find(item => item.id === newComment.id)
            }

            // If it wasn't reply
            if (!replyToComment) {
                if (
                    isExistingComment(
                        state.selectedItemComments,
                        action.payload,
                    )
                ) {
                    return
                }

                state.selectedItemComments = [
                    action.payload,
                    ...state.selectedItemComments,
                ]

                return
            }

            state.selectedItemComments = state.selectedItemComments.map(
                item => {
                    if (
                        item.id !== replyToComment.id ||
                        isExistingComment(item.replies || [], action.payload)
                    ) {
                        return item
                    }

                    return {
                        ...item,
                        replies: [...(item.replies || []), action.payload],
                    }
                },
            )
        },

        policiesDeletedSelectedItemComment: (
            state,
            action: PayloadAction<{ deletedId: string }>,
        ) => {
            state.selectedItemComments = state.selectedItemComments
                .filter(item => item.id !== action.payload.deletedId)
                .map(item =>
                    !item.replies?.length
                        ? item
                        : {
                              ...item,

                              replies: item.replies.filter(
                                  innerItem =>
                                      innerItem.id !== action.payload.deletedId,
                              ),
                          },
                )
        },

        policiesUpdatedSelectedItemComment: (
            state,
            action: PayloadAction<IOneVerifyPolicyComment>,
        ) => {
            state.selectedItemComments = state.selectedItemComments.map(item =>
                item.id === action.payload.id
                    ? action.payload
                    : item.replies?.length
                      ? {
                            ...item,
                            replies: item.replies.map(innerItem =>
                                innerItem.id === action.payload.id
                                    ? action.payload
                                    : innerItem,
                            ),
                        }
                      : item,
            )
        },

        policiesUpdatedSelectedItemEhrWriteStatus: (
            state,
            action: PayloadAction<IOneVerifyEhrWriteStatusUpdatedSocketsPayload>,
        ) => {
            const updateListEhrWriteLog = (
                list: IOneVerifyCoverageCheckHistory[],
            ) => {
                list.forEach(item => {
                    if (item.policy?.id === action.payload.policyId) {
                        item.policy.ehrWriteLog = action.payload.ehrWriteLog
                    }
                })
            }

            if (state.selectedItem?.policy?.id === action.payload.policyId) {
                state.selectedItem.policy.ehrWriteLog =
                    action.payload.ehrWriteLog

                state.selectedItemHistory.forEach(item => {
                    if (item.policy?.id === action.payload.policyId) {
                        item.policy.ehrWriteLog = action.payload.ehrWriteLog
                    }
                })
            }

            updateListEhrWriteLog(state.reportsItems)
            updateListEhrWriteLog(state.medicaidItems)
        },

        // COMMON LIST
        policiesUpdateListItems: (
            state,
            action: PayloadAction<{
                listType: PoliciesListType
                updatedItems: IOneVerifyCoverageCheckHistory[]
            }>,
        ) => {
            const updateListItems = (
                list: IOneVerifyCoverageCheckHistory[],
                updatedItems: IOneVerifyCoverageCheckHistory[],
            ): IOneVerifyCoverageCheckHistory[] => {
                for (const updatedItem of updatedItems) {
                    const index = list.findIndex(
                        item =>
                            item.coverageResult.resultId ===
                            updatedItem.coverageResult.resultId,
                    )

                    if (index < 0) {
                        continue
                    }

                    list[index] = updatedItem
                }

                return list
            }

            switch (action.payload.listType) {
                case PoliciesListType.HISTORY_SEARCH:
                    state.historyItems = updateListItems(
                        state.historyItems,
                        action.payload.updatedItems,
                    )

                    break

                case PoliciesListType.REPORTS:
                    state.reportsItems = updateListItems(
                        state.reportsItems,
                        action.payload.updatedItems,
                    )
                    break

                case PoliciesListType.OVERRIDES:
                    state.overridesItems = updateListItems(
                        state.overridesItems,
                        action.payload.updatedItems,
                    )

                    break

                case PoliciesListType.PLAN_YEAR_RESETS:
                    state.planYearResetsItems = updateListItems(
                        state.planYearResetsItems,
                        action.payload.updatedItems,
                    )

                    break

                case PoliciesListType.MEDICAID:
                    state.medicaidItems = updateListItems(
                        state.medicaidItems,
                        action.payload.updatedItems,
                    )

                    break

                case PoliciesListType.AGENTIC:
                    state.agenticItems = updateListItems(
                        state.agenticItems,
                        action.payload.updatedItems,
                    )

                    break

                case PoliciesListType.TASK_ASSIGNMENTS:
                case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
                case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                    state.taskAssignmentsItems = updateListItems(
                        state.taskAssignmentsItems,
                        action.payload.updatedItems,
                    )

                    break

                case PoliciesListType.ASSIGNED_TASKS:
                case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
                case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                    state.assignedTasksItems = updateListItems(
                        state.assignedTasksItems,
                        action.payload.updatedItems,
                    )

                    break
            }
        },

        // SEARCH / HISTORY

        policiesSetHistoryFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.historyFilters = action.payload
        },

        policiesSetHistorySort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.historySort = action.payload
        },

        policiesSetHistoryList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.historyItems = action.payload.items
            state.historyPagination = action.payload.newPagination
            state.historyIsError = action.payload.isError
        },

        // REPORTS

        policiesSetReportsList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.reportsItems = action.payload.items
            state.reportsPagination = action.payload.newPagination
            state.reportsIsError = action.payload.isError
        },

        policiesSetReportsFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            if (action.payload.flags?.length) {
                state.visualizationsCollapseTrigger = new Date().getTime()
            }

            state.reportsFilters = action.payload
        },

        policiesSetReportsSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.reportsSort = action.payload
        },

        policiesSetReportsAggregationsStatusCodes: (
            state,
            action: PayloadAction<ICoverageHistoryAggregationsStatusCodes | null>,
        ) => {
            state.reportsAggregationsStatusCodes = action.payload
        },

        policiesSetReportsAggregationsFlags: (
            state,
            action: PayloadAction<ICoverageHistoryAggregationsFlags | null>,
        ) => {
            state.reportsAggregationsFlags = action.payload
        },

        // OVERRIDES

        policiesSetOverridesList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.overridesItems = action.payload.items
            state.overridesPagination = action.payload.newPagination
            state.overridesIsError = action.payload.isError
        },

        policiesSetOverridesFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.overridesFilters = action.payload
        },

        policiesSetOverridesSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.overridesSort = action.payload
        },

        // PLAN YEAR RESETS

        policiesSetPlanYearResetsList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.planYearResetsItems = action.payload.items
            state.planYearResetsPagination = action.payload.newPagination
            state.planYearResetsIsError = action.payload.isError
        },

        policiesSetPlanYearResetsFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.planYearResetsFilters = action.payload
        },

        policiesSetPlanYearResetsSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.planYearResetsSort = action.payload
        },

        policiesSetPlanYearResetsAggregations: (
            state,
            action: PayloadAction<ICoverageHistoryAggregationsPlanYearResets | null>,
        ) => {
            state.planYearResetsAggregations = action.payload
        },

        // MEDICAID REPORTS

        policiesSetMedicaidList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.medicaidItems = action.payload.items
            state.medicaidPagination = action.payload.newPagination
            state.medicaidIsError = action.payload.isError
        },

        policiesSetMedicaidFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            if (action.payload.flags?.length) {
                state.visualizationsCollapseTrigger = new Date().getTime()
            }

            state.medicaidFilters = action.payload
        },

        policiesSetMedicaidSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.medicaidSort = action.payload
        },

        policiesSetMedicaidAggregationsPlanType: (
            state,
            action: PayloadAction<ICoverageHistoryAggregationsMedicaidPlanType | null>,
        ) => {
            state.medicaidAggregationsPlanType = action.payload
        },

        policiesSetMedicaidAggregationsActivePlanType: (
            state,
            action: PayloadAction<ICoverageHistoryAggregationsMedicaidActivePlanType | null>,
        ) => {
            state.medicaidAggregationsActivePlanType = action.payload
        },

        // AGENTIC

        policiesSetAgenticList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.agenticItems = action.payload.items
            state.agenticPagination = action.payload.newPagination
            state.agenticIsError = action.payload.isError
        },

        policiesSetAgenticFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.agenticFilters = action.payload
        },

        policiesSetAgenticSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.agenticSort = action.payload
        },

        // TASKS

        policiesSetTaskAssignmentsList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.taskAssignmentsItems = action.payload.items
            state.taskAssignmentsPagination = action.payload.newPagination
            state.taskAssignmentsIsError = action.payload.isError
        },

        policiesSetTaskAssignmentsFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.taskAssignmentsFilters = action.payload
        },

        policiesSetTaskAssignmentsSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.taskAssignmentsSort = action.payload
        },

        policiesGetAssignedTasksList: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.assignedTasksFilters = action.payload
        },

        policiesSetAssignedTasksList: (
            state,
            action: PayloadAction<ICoverageHistoryListResult>,
        ) => {
            state.assignedTasksItems = action.payload.items
            state.assignedTasksPagination = action.payload.newPagination
            state.assignedTasksIsError = action.payload.isError
        },

        policiesSetAssignedTasksFilters: (
            state,
            action: PayloadAction<IPoliciesListFiltersData>,
        ) => {
            state.assignedTasksFilters = action.payload
        },

        policiesSetAssignedTasksSort: (
            state,
            action: PayloadAction<ICoverageHistoryListSort>,
        ) => {
            state.assignedTasksSort = action.payload
        },
    },
    // Must add such approach if async thunk needs to be aborted, and add data only if fulfilled
    extraReducers: builder => {
        builder.addCase(
            policiesGetReportsAggregationsStatusCodes.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetReportsAggregationsStatusCodes(
                    state,
                    action,
                )
            },
        )

        builder.addCase(
            policiesGetReportsAggregationsFlags.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetReportsAggregationsFlags(
                    state,
                    action,
                )
            },
        )

        builder.addCase(
            policiesGetPlanYearResetsAggregations.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetPlanYearResetsAggregations(
                    state,
                    action,
                )
            },
        )

        builder.addCase(
            policiesGetMedicaidReportsAggregationsPlanType.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetMedicaidAggregationsPlanType(
                    state,
                    action,
                )
            },
        )

        builder.addCase(
            policiesGetMedicaidReportsAggregationsActivePlanType.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetMedicaidAggregationsActivePlanType(
                    state,
                    action,
                )
            },
        )

        builder.addCase(policiesGetHistoryList.fulfilled, (state, action) => {
            policiesSlice.caseReducers.policiesSetHistoryList(state, action)
        })

        builder.addCase(policiesGetReportsList.fulfilled, (state, action) => {
            policiesSlice.caseReducers.policiesSetReportsList(state, action)
        })

        builder.addCase(policiesGetOverridesList.fulfilled, (state, action) => {
            policiesSlice.caseReducers.policiesSetOverridesList(state, action)
        })

        builder.addCase(
            policiesGetPlanYearResetsList.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetPlanYearResetsList(
                    state,
                    action,
                )
            },
        )

        builder.addCase(policiesGetMedicaidList.fulfilled, (state, action) => {
            policiesSlice.caseReducers.policiesSetMedicaidList(state, action)
        })

        builder.addCase(policiesGetAgenticList.fulfilled, (state, action) => {
            policiesSlice.caseReducers.policiesSetAgenticList(state, action)
        })

        builder.addCase(
            policiesGetAssignedTasksList.fulfilled,
            (state, action) => {
                policiesSlice.caseReducers.policiesSetAssignedTasksList(
                    state,
                    action,
                )
            },
        )

        builder.addCase(taskAssignmentsGetList.fulfilled, (state, action) => {
            policiesSlice.caseReducers.policiesSetTaskAssignmentsList(
                state,
                action,
            )
        })
    },
})

export const {
    policiesReset,
    policiesSetVisualizationsCollapseTrigger,
    policiesSetHeaderDragDropDisabled,
    policiesSetGettingFullListProgress,
    policiesIncreaseGetFullListProgress,

    policiesResetSelectedItem,
    policiesSetSelectedItem,

    policiesAddSelectedItemHistory,
    policiesUpdateSelectedItemHistory,

    policiesSetSelectedItemComments,
    policiesAddedSelectedItemComments,
    policiesDeletedSelectedItemComment,
    policiesUpdatedSelectedItemComment,

    policiesUpdatedSelectedItemEhrWriteStatus,

    policiesUpdateListItems,

    policiesSetHistoryFilters,
    policiesSetHistoryList,
    policiesSetHistorySort,

    policiesSetReportsList,
    policiesSetReportsFilters,
    policiesSetReportsSort,

    policiesSetOverridesList,
    policiesSetOverridesFilters,
    policiesSetOverridesSort,

    policiesSetPlanYearResetsList,
    policiesSetPlanYearResetsFilters,
    policiesSetPlanYearResetsSort,

    policiesSetMedicaidList,
    policiesSetMedicaidFilters,
    policiesSetMedicaidSort,

    policiesSetAgenticList,
    policiesSetAgenticFilters,
    policiesSetAgenticSort,

    policiesSetAssignedTasksList,
    policiesSetAssignedTasksFilters,
    policiesSetAssignedTasksSort,

    policiesSetTaskAssignmentsList,
    policiesSetTaskAssignmentsFilters,
    policiesSetTaskAssignmentsSort,
} = policiesSlice.actions

export const policiesReducer = policiesSlice.reducer
